import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Menu, X, ShoppingCart, RefreshCw } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import CartDrawer from '@/components/cart/CartDrawer';
import Logo from './Logo';
import { useProducts } from '@/contexts/ProductContext';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { getCategories } from '@/lib/categories';
import { Category } from '@/lib/types';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);
  const { itemCount } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshProducts, isSyncing, lastSyncTime } = useProducts();

  const { data: categories = [], isLoading: isLoadingCategories } = useQuery<Category[], Error>({
      queryKey: ['categoriesForHeader'],
      queryFn: getCategories,
      staleTime: 1000 * 60 * 5
  });

  useEffect(() => {
    setIsMenuOpen(false);
    setOpenCategoryId(null);
  }, [location]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setIsMenuOpen(false);
      navigate(`/catalog?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const handleCategoryClick = (categoryId: string) => {
    setIsMenuOpen(false);
    setOpenCategoryId(null);
    navigate(`/catalog?category=${categoryId}`);
  };

  const handleSubcategoryClick = (categoryId: string, subcategoryId: string) => {
    setIsMenuOpen(false);
    setOpenCategoryId(null);
    navigate(`/catalog?category=${categoryId}&subcategory=${subcategoryId}`);
  };

  const toggleCategoryDropdown = (categoryId: string) => {
    setOpenCategoryId(openCategoryId === categoryId ? null : categoryId);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        {/* Top Bar */}
        <div className="border-b border-gray-200">
          <div className="container mx-auto px-4 h-20 flex items-center justify-between">
            <Logo size="md" withText={true} />

            <div className="flex items-center space-x-4">
              <form onSubmit={handleSearchSubmit} className="hidden md:flex relative">
                <input
                  type="text"
                  placeholder="Buscar produtos..."
                  className="pl-3 pr-10 py-2 rounded-full bg-gray-100 focus:outline-none focus:ring-2 focus:ring-navy-500 text-sm w-48"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button type="submit" className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500" aria-label="Buscar">
                  <Search size={16} />
                </button>
              </form>
              <button
                onClick={() => setIsCartOpen(true)}
                className="relative group flex items-center justify-center w-10 h-10 rounded-full bg-orange-50 text-orange-500 hover:bg-orange-100 transition-colors"
                aria-label={`Carrinho com ${itemCount} itens`}
              >
                <ShoppingCart size={20} />
                {itemCount > 0 && (
                  <span className="animate-bubble absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 rounded-full bg-navy-600 text-white text-xs">
                    {itemCount}
                  </span>
                )}
              </button>
              <Button
                variant="ghost"
                size="icon"
                onClick={refreshProducts}
                disabled={isSyncing}
                title={lastSyncTime ? `Última atualização: ${lastSyncTime.toLocaleTimeString()}` : 'Sincronizar produtos'}
                className="hidden md:inline-flex"
              >
                <RefreshCw className={`h-5 w-5 ${isSyncing ? 'animate-spin text-aqua-600' : 'text-gray-500'}`} />
              </Button>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-600"
                aria-label="Abrir menu"
                aria-expanded={isMenuOpen}
              >
                {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Desktop Navigation Bar - Categories */}
        <nav className="hidden md:block bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="container mx-auto px-4">
            <div className="flex items-center space-x-1">
              {/* All Products Link */}
              <Link
                to="/catalog"
                className="px-4 py-3 text-sm font-medium hover:bg-white/10 transition-colors rounded-sm"
              >
                Todos os Produtos
              </Link>

              {/* Category Links with Dropdowns */}
              {isLoadingCategories ? (
                <div className="px-4 py-3 text-sm">Carregando...</div>
              ) : (
                categories.map((category) => (
                  <div key={category.id} className="relative group">
                    <button
                      onClick={() => handleCategoryClick(category.id)}
                      className="px-4 py-3 text-sm font-medium hover:bg-white/10 transition-colors rounded-sm flex items-center space-x-1"
                    >
                      <span>{category.name}</span>
                      {category.subcategories && category.subcategories.length > 0 && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </button>

                    {/* Dropdown for Subcategories */}
                    {category.subcategories && category.subcategories.length > 0 && (
                      <div className="absolute left-0 top-full hidden group-hover:block bg-white text-gray-900 shadow-lg rounded-b-md min-w-[200px] z-50 border-t-2 border-blue-600">
                        <div className="py-2">
                          {/* View All in Category */}
                          <button
                            onClick={() => handleCategoryClick(category.id)}
                            className="w-full text-left px-4 py-2 text-sm font-semibold text-blue-600 hover:bg-blue-50 transition-colors"
                          >
                            Ver todos em {category.name}
                          </button>
                          <div className="border-t border-gray-200 my-1"></div>
                          {/* Subcategories */}
                          {category.subcategories.map((subcategory) => (
                            <button
                              key={subcategory.id}
                              onClick={() => handleSubcategoryClick(category.id, subcategory.id)}
                              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors"
                            >
                              {subcategory.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </nav>

        {/* Mobile Menu */}
        <div className={`md:hidden ${isMenuOpen ? 'block' : 'hidden'} absolute top-full left-0 right-0 bg-white shadow-md border-t border-gray-100 max-h-[calc(100vh-5rem)] overflow-y-auto`}>
          <div className="px-4 py-4">
            {/* Mobile Search */}
            <form onSubmit={handleSearchSubmit} className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar produtos..."
                  className="w-full pl-3 pr-10 py-2 rounded-full bg-gray-100 focus:outline-none focus:ring-2 focus:ring-navy-500 text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button type="submit" className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500" aria-label="Buscar">
                  <Search size={16} />
                </button>
              </div>
            </form>

            {/* Mobile Categories */}
            <div className="space-y-2">
              <Link
                to="/catalog"
                className="block px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                📦 Todos os Produtos
              </Link>

              {isLoadingCategories ? (
                <div className="px-4 py-3 text-sm text-gray-500">Carregando categorias...</div>
              ) : (
                categories.map((category) => (
                  <div key={category.id} className="border-b border-gray-200 last:border-0">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => handleCategoryClick(category.id)}
                        className="flex-1 text-left px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        {category.name}
                      </button>
                      {category.subcategories && category.subcategories.length > 0 && (
                        <button
                          onClick={() => toggleCategoryDropdown(category.id)}
                          className="px-3 py-3 text-gray-500 hover:text-gray-900"
                          aria-label={`Toggle ${category.name} subcategories`}
                        >
                          <svg
                            className={`w-5 h-5 transition-transform ${openCategoryId === category.id ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* Mobile Subcategories */}
                    {category.subcategories && category.subcategories.length > 0 && openCategoryId === category.id && (
                      <div className="pl-4 pb-2 space-y-1">
                        {category.subcategories.map((subcategory) => (
                          <button
                            key={subcategory.id}
                            onClick={() => handleSubcategoryClick(category.id, subcategory.id)}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                          >
                            → {subcategory.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </header>

      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </>
  );
}

export default Header;
